import { Transition } from '@headlessui/react';
import { useState, useEffect } from 'react';
import { StateType } from 'src/redux/reducers';
import { useDispatch, useSelector } from 'react-redux';

import { REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB_PREVIEW } from 'src/redux/reducers/playlistDisplayHub';
import { HandleMediaOptions } from '../DisplayHubStory/types';

import DisplayHubControlForm from 'src/components/DisplayHubControlForm';
import GraphicMediaLibrary from 'src/components/GraphicMediaLibrary';
import DisplayHubStories from 'src/components/DisplayHubStories';
import useKeypress from 'src/hooks/useKeypress';

import styles from './DisplayHubDetails.module.scss';

type Props = {
  handleCreateStory: () => void;
};

export default function DisplayHubDetails({ handleCreateStory }: Props) {
  const dispatch = useDispatch();

  const [mediaLibraryState, setMediaLibraryState] = useState<{ open: boolean; onClose?: Function }>(
    { open: false, onClose: () => {} }
  );
  const [canHideLibrary, setCanHideLibrary] = useState<boolean>(true);

  const { selectedPlaylistDisplayHub } = useSelector(
    (state: StateType) => state.playlistDisplayHub
  );

  useEffect(() => {
    handleClickOutsideDrawer();
  }, [selectedPlaylistDisplayHub?.id]);

  const handleUpdatePlaylistGraphicInSandbox = (properties: Properties, url: string | null) =>
    dispatch({
      type: REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB_PREVIEW,
      payload: { images: { [properties.id]: url } },
    });

  const handleMedia = (properties: ImageProperties, options: HandleMediaOptions) => {
    const { mode, callback } = options;

    if (mode === 'remove') {
      callback?.(null);

      // This timeout is necessary to allow multiple dispatches of the same type.
      // Without it, only the last dispatch would be executed.
      setTimeout(() => handleUpdatePlaylistGraphicInSandbox(properties, null), 10);
      return;
    }

    if (mode === 'select') {
      const onClose = (url: string) => {
        callback?.(url);

        // This timeout is necessary to allow multiple dispatches of the same type.
        // Without it, only the last dispatch would be executed.
        setTimeout(() => handleUpdatePlaylistGraphicInSandbox(properties, url), 10);

        if (selectedPlaylistDisplayHub) {
          setMediaLibraryState({ open: false, onClose: () => {} });
        }
      };

      setMediaLibraryState({ open: true, onClose });
    }
  };

  const handleMediaSelected = (url: string) => mediaLibraryState.onClose?.(url);

  const handleClickOutsideDrawer = () => setMediaLibraryState({ open: false, onClose: () => {} });

  useKeypress('Escape', {}, () => canHideLibrary && handleClickOutsideDrawer());

  if (!selectedPlaylistDisplayHub) return null;

  const slideOutOpen = mediaLibraryState.open;

  return (
    <div className={styles.Container}>
      <DisplayHubControlForm selectedDisplayHub={selectedPlaylistDisplayHub} />
      <div className={styles.Content}>
        <DisplayHubStories
          selectedPlaylistDisplayHub={selectedPlaylistDisplayHub}
          handleCreateStory={handleCreateStory}
          handleMedia={handleMedia}
        />
        <Transition.Root show={slideOutOpen} className={styles.SlideOutContainer}>
          <Transition.Child
            className={styles.Backdrop}
            onClick={handleClickOutsideDrawer}
            as="div"
            leave="transition-opacity"
            leaveFrom="opacity-25"
            leaveTo="opacity-0"
          />
          <Transition.Child
            className={styles.SlideOutContent}
            enter="transition-all duration-100"
            enterFrom="transform translate-x-[75%]"
            enterTo="transform translate-x-0"
            leave="transition-all duration-100"
            leaveFrom="transform translate-x-0"
            leaveTo="transform translate-x-[75%]"
          >
            {mediaLibraryState.open && (
              <GraphicMediaLibrary
                setCanHideLibrary={setCanHideLibrary}
                onFileSelect={handleMediaSelected}
              />
            )}
          </Transition.Child>
        </Transition.Root>
      </div>
    </div>
  );
}
